<template>
  <div>
    <Button
      v-if="showButton"
      label="Send Email"
      size="small"
      @click="toggleOpen"
    >
      <template #icon>
        <Icon name="material-symbols:mail" />
      </template>
    </Button>

    <Dialog
      v-model:visible="visible"
      header="Send Email"
      :style="{ width: '600px' }"
      :modal="true"
    >
      <div class="grid grid-cols-1 gap-y-5">
        <div class="grid grid-cols-1">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Recipient Email
          </label>
          <InputText
            v-model="payload.recipient"
            placeholder="Enter recipient email"
            class="w-full"
          />
        </div>
        <MessageTemplateSelector
          v-model:selected-template-id="selectedTemplateId"
          type="EMAIL"
          @select="onTemplateSelect"
        />

        <div class="grid grid-cols-1">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Subject
          </label>
          <InputText
            v-model="payload.subject"
            placeholder="Enter email subject"
            class="w-full"
            :disabled="isResolving"
          />
        </div>
        <div class="relative">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Email body
          </label>
          <div class="relative">
            <WidgetsRichTextEditor
              v-model:data="payload.body"
              placeholder="Enter email message"
              rows="4"
              class="w-full"
            />
            <div
              v-if="isResolving"
              class="absolute inset-0 flex items-center justify-center bg-white/70 rounded-md"
            >
              <i class="pi pi-spin pi-spinner text-xl text-gray-500" />
            </div>
          </div>
        </div>
        <div class="flex justify-end">
          <Button
            label="Send Email"
            class="mt-4"
            :loading="isLoading"
            @click="sendEmail"
          />
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import type { MessageTemplate } from '@prisma/client'
import { useAuthStore } from '~/store/auth'

const visible = ref(false)
const authStore = useAuthStore()

const props = defineProps<{
  recipient: string
  contactId: string
  businessId?: string
}>()

const showButton = defineModel<boolean>('showButton', {
  required: false,
  default: true,
})

const toggleOpen = () => {
  visible.value = !visible.value
}

const payload = ref({
  recipient: '',
  subject: '',
  body: '<p class="h-24"></p>',
})

const selectedTemplateId = ref<string | null>(null)
const isResolving = ref(false)
const { resolve: resolveTemplate } = useMessageTemplate()

const onTemplateSelect = async (template: MessageTemplate | null) => {
  if (template) {
    isResolving.value = true
    const { data } = await resolveTemplate({
      templateId: template.id,
      contactId: props.contactId,
      businessId: props.businessId,
    })
    payload.value.subject = data?.subject ?? template.subject ?? ''
    payload.value.body = data?.body ?? template.body
    isResolving.value = false
  }
}

defineExpose({
  toggleOpen,
})
const { getUserWorkspace: getUserWorkSpaceFromDB } = useUserWorkSpace()
const { createNote } = useNotes()
const toast = useToast()

const emit = defineEmits(['refresh'])

const isLoading = ref(false)
const sendEmail = async () => {
  isLoading.value = true

  const businessId = props.businessId

  const activeUserRes = await getUserWorkSpaceFromDB()
  const _activeUser = activeUserRes.data

  // Collect reply-to components separately
  const replyToEmail = _activeUser?.email ?? null
  const replyToName = _activeUser?.name ?? null

  // Build replyTo as string only (no JSON object)
  // If name exists, we could use it later, but for now always send email string
  const replyTo = replyToEmail ?? null

  try {
    await $fetch('/api/v1/email-job/create', {
      method: 'POST',
      body: {
        contactId: props.contactId,
        businessId,
        recipientEmail: payload.value.recipient,
        subject: payload.value.subject,
        body: payload.value.body,
        sendCategory: 'OUTREACH',
        replyTo,
      },
    })
  }
  catch (e: any) {
    toast.add({ severity: 'error', summary: 'Failed', detail: e?.data?.statusMessage || 'Failed to send email', life: 3000 })
    isLoading.value = false
    return
  }

  await createNote(
    {
      type: 'Email',
      content: 'Sent an email below: <br>' + payload.value.body,
      showOnVendorReport: false,
      attachments: [],
    },
    props.contactId,
    businessId,
  )

  emit('refresh')
  visible.value = false
  isLoading.value = false
}

onMounted(() => {
  payload.value.recipient = props.recipient
})
</script>
